import { useEffect, useState } from "react";
import axios from "axios";
import { Calendar, Clock, TrendingUp, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ArrivedContextSidePanel · Founder Op Experience V1
 * ---------------------------------------------------
 * Desktop-only side panel (>=lg breakpoint) that fills the empty
 * left column on the ARRIVED stage of Work Mode. Mobile is
 * untouched — this component renders nothing below lg.
 *
 * Purpose: give the instructor at-a-glance context alongside the
 * Dynamic Briefing without polluting the calm mobile layout.
 * Read-only. Reuses the /work-mode/dynamic-briefing endpoint so
 * no new backend calls are added.
 */
export default function ArrivedContextSidePanel({ studentId, pupilName }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    axios
      .get(`${API}/api/work-mode/dynamic-briefing`, {
        params: { student_id: studentId },
        withCredentials: true,
      })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [studentId]);

  const recentCount = data?.pupil?.recent_lesson_count ?? null;
  const respLevel = data?.objective?.responsibility_level;
  const respLabel = ({
    shared_low: "Shared — you lead",
    shared_medium: "Shared — 50 / 50",
    learner_led: "Learner-led",
  })[respLevel] || "Shared";

  return (
    <aside
      data-testid="arrived-context-side"
      className="hidden lg:flex flex-col gap-4 w-72 flex-shrink-0"
    >
      {/* Pupil at-a-glance */}
      <div className="bg-slate-800/60 border border-slate-700 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-3">
          <User className="w-4 h-4 text-slate-400" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Pupil at a glance
          </p>
        </div>
        <p className="text-white text-lg font-extrabold leading-tight">
          {pupilName || "This pupil"}
        </p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <div className="bg-slate-900/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-sky-400" />
              <p className="text-[10px] uppercase text-slate-500 tracking-wide">
                Lessons
              </p>
            </div>
            <p className="text-white text-xl font-bold mt-0.5">
              {recentCount ?? "—"}
            </p>
          </div>
          <div className="bg-slate-900/50 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
              <p className="text-[10px] uppercase text-slate-500 tracking-wide">
                Level
              </p>
            </div>
            <p className="text-white text-sm font-bold mt-0.5 truncate">
              {respLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Session helper */}
      <div className="bg-indigo-950/40 border border-indigo-700/30 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-indigo-300" />
          <p className="text-[11px] font-bold uppercase tracking-wider text-indigo-300/80">
            Session pacing
          </p>
        </div>
        <ul className="space-y-1.5 text-sm text-slate-300 leading-snug">
          <li>• Warm-up: 5 min conversation</li>
          <li>• Objective work: 40 min</li>
          <li>• Debrief in car: 10 min</li>
          <li>• Wrap-up + next steps: 5 min</li>
        </ul>
      </div>

      <p className="text-[10px] text-slate-500 text-center pt-1">
        Side panel · desktop only
      </p>
    </aside>
  );
}
